import { useState } from 'react';
import type { SeriesMetric, SeriesRange } from '@st/contract';
import { useSeries } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { useTranslation } from '../../i18n/I18nProvider';
import { formatDay } from '../../lib/format';
import {
  SERIES_METRICS,
  SERIES_RANGES,
  formatChange,
  formatMetric,
  isSeriesMetric,
  metricLabel,
  rangeLabel,
} from '../../lib/series';
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  SegmentedControl,
  Select,
  SeriesChart,
  SkeletonCard,
  StatTile,
} from '../../ui';

export interface MetricChartSectionProps {
  /** Trener gleda tuđe podatke; klijent svoje. */
  clientId?: string;
  defaultMetric?: SeriesMetric;
}

/**
 * Napredni graf: prebacivanje raspona i metrike, sažetak razdoblja ispod.
 *
 * Sažetak (promjena, min, max, prosjek) dolazi iz `summary` sa servera i NIKAD
 * se ne računa ovdje — dva klijenta na istim podacima moraju vidjeti isti broj.
 * Filteri stoje u jednom redu iznad grafa i obuhvaćaju sve ispod sebe.
 */
export function MetricChartSection({ clientId, defaultMetric = 'weight' }: MetricChartSectionProps) {
  const { t, locale } = useTranslation();
  const [metric, setMetric] = useState<SeriesMetric>(defaultMetric);
  const [range, setRange] = useState<SeriesRange>('3m');
  const [tableOpen, setTableOpen] = useState(false);

  const series = useSeries(metric, range, clientId);
  const title = metricLabel(metric, t);

  return (
    <Card>
      <CardHeader title={title} eyebrow={t('progress.charts')} />

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <SegmentedControl
          ariaLabel={t('chart.range')}
          value={range}
          onChange={setRange}
          options={SERIES_RANGES.map((value) => ({ value, label: rangeLabel(value, t) }))}
        />
        <Select
          id="chart-metric"
          aria-label={t('chart.metric')}
          value={metric}
          onChange={(e) => {
            const next = e.target.value;
            if (isSeriesMetric(next)) setMetric(next);
          }}
          className="max-w-[14rem]"
          options={SERIES_METRICS.map((value) => ({ value, label: metricLabel(value, t) }))}
        />
      </div>

      <QueryBoundary
        query={series}
        skeleton={<SkeletonCard />}
        isEmpty={(d) => d.points.length === 0}
        empty={{ message: t('chart.noData') }}
      >
        {(data) => (
          <div className="flex flex-col gap-5">
            {data.points.length === 1 ? (
              <EmptyState message={t('chart.onePoint')} />
            ) : (
              <SeriesChart
                points={data.points}
                ariaLabel={title}
                unit={data.unit}
                formatDay={(day) => formatDay(`${day}T00:00:00.000Z`, locale)}
                formatValue={(value) => formatMetric(value, metric)}
              />
            )}

            <div>
              <p className="eyebrow mb-2">
                {t('chart.summaryPeriod', {
                  from: formatDay(`${data.from}T00:00:00.000Z`, locale),
                  to: formatDay(`${data.to}T00:00:00.000Z`, locale),
                })}
              </p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {/* Predznak je već u vrijednosti — `delta` bi ponovio isti broj. */}
                <StatTile
                  label={t('chart.change')}
                  value={data.summary.change === null ? '—' : formatChange(data.summary.change, metric)}
                  unit={data.unit}
                />
                <StatTile
                  label={t('chart.min')}
                  value={data.summary.min === null ? '—' : formatMetric(data.summary.min, metric)}
                  unit={data.unit}
                />
                <StatTile
                  label={t('chart.max')}
                  value={data.summary.max === null ? '—' : formatMetric(data.summary.max, metric)}
                  unit={data.unit}
                />
                <StatTile
                  label={t('chart.average')}
                  value={
                    data.summary.average === null ? '—' : formatMetric(data.summary.average, metric)
                  }
                  unit={data.unit}
                />
              </div>
            </div>

            {/* Tablični prikaz: svaka vrijednost dostupna i bez pokazivača. */}
            <div>
              <Button size="sm" variant="ghost" onClick={() => setTableOpen((v) => !v)}>
                {tableOpen ? t('chart.hideTable') : t('chart.showTable')}
              </Button>
              {tableOpen && (
                <div className="mt-3 max-h-64 overflow-auto rounded-md border border-line-subtle">
                  <table className="w-full text-[13px]">
                    <caption className="sr-only">{title}</caption>
                    <thead className="sticky top-0 bg-bg-alt">
                      <tr>
                        <th scope="col" className="px-3 py-2 text-left font-normal text-ink-muted">
                          {t('chart.day')}
                        </th>
                        <th scope="col" className="px-3 py-2 text-right font-normal text-ink-muted">
                          {t('chart.value')} {data.unit && `(${data.unit})`}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.points.map((point) => (
                        <tr key={point.day} className="border-t border-line-subtle">
                          <td className="px-3 py-1.5 text-ink">
                            {formatDay(`${point.day}T00:00:00.000Z`, locale)}
                          </td>
                          <td className="px-3 py-1.5 text-right text-ink tabular-nums">
                            {formatMetric(point.value, metric)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </QueryBoundary>
    </Card>
  );
}
